/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, MouseEvent, ReactElement, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DashboardPlateNumberBlock from '../../components/DashboardPlateNumberBlock/DashboardPlateNumberBlock';
import { DashboardPlates, dashboardPreventId } from '../../constants/dashboard';
import { dashboardStore } from '../../store/dashboardStore';
import { Icons } from '~/assets/fonts/Icons/icons';
import theme from '~/constants/styles';
import { localeStore } from '~/mobx/localesStore';
import { ICustomTooltipProps } from '~/view/components/Controls/CustomTooltip/CustomTooltip';
import Icon from '~/view/components/Controls/Icon/Icon';
import TooltipIcon from '~/view/components/TooltipIcon/TooltipIcon';
import './style.scss';

interface IProps {
  type: DashboardPlates;
  title: string;
  mainHeader: string;
  mainHeaderTooltip: Partial<ICustomTooltipProps>;
  secondaryHeader: string;
  secondaryHeaderTooltip: Partial<ICustomTooltipProps>;
  conclusionHeader: string;
  conclusionHeaderTooltip: Partial<ICustomTooltipProps>;
  linkPath: string;
  linkTitle: string;
}

const DashboardPlate: FC<IProps> = observer(({ type, ...props }: IProps): ReactElement => {
  const { platesData, dashboardPermission } = dashboardStore;

  const isNotSelectablePlate = useMemo((): boolean => !dashboardPermission[type].showDetails, [type]);
  const isActive = useMemo((): boolean => dashboardStore.selectedPlate === type, [dashboardStore.selectedPlate]);

  const conclusionHeader = useMemo((): string => {
    const percentSymbol: string = type === DashboardPlates.MARKETING ? '' : '%';

    return localeStore.t(props.conclusionHeader) + ' ' + String(platesData[type].conclusionValue) + percentSymbol;
  }, [props.conclusionHeader, type, platesData]);

  const onSelectPlate = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.preventDefault();

      if (isNotSelectablePlate) return;
      const target = event.target as HTMLElement;
      const availableChoose: boolean = target.getAttribute('id') !== dashboardPreventId && !target.className.includes('tooltip');

      if (availableChoose) dashboardStore.selectPlate(type);
    },
    [isNotSelectablePlate]
  );

  const className = useMemo((): string => {
    return classNames('dashboard-plate', { 'dashboard-plate_active': isActive, 'cursor-pointer': !isNotSelectablePlate });
  }, [isActive, isNotSelectablePlate]);

  return (
    <div onClick={onSelectPlate} className={className}>
      <div className="d-flex flex-row align-items-center justify-content-between mb-3">
        <p className="dashboard-plate__title">{localeStore.t(props.title)}</p>
        {isNotSelectablePlate ? null : (
          <Icon name={Icons.Chevron} size={24} color={theme.colors[isActive ? 'mainblue' : 'gray2']} className="dashboard-plate__chevron mr-4" />
        )}
      </div>

      <div className="d-flex flex-column full-height gap-15">
        <DashboardPlateNumberBlock
          header={props.mainHeader}
          tooltip={props.mainHeaderTooltip}
          value={platesData[type].mainValue}
          percent={platesData[type].mainPercent}
        />
        <DashboardPlateNumberBlock
          header={props.secondaryHeader}
          tooltip={props.secondaryHeaderTooltip}
          value={platesData[type].secondaryValue}
          percent={platesData[type].secondaryPercent}
        />
      </div>

      <div className="d-flex flex-column gap-15">
        <div className="d-flex flex-row align-items-center gap-10">
          <p className="dashboard-plate__header-conclusion">{conclusionHeader}</p>
          <TooltipIcon {...props.conclusionHeaderTooltip} iconId={dashboardPreventId} iconSize={20} />
        </div>

        {dashboardPermission[type].showLink ? (
          <Link id={dashboardPreventId} to={props.linkPath}>
            <span id={dashboardPreventId} className="dashboard-plate__link">
              {localeStore.t(props.linkTitle)}
            </span>
          </Link>
        ) : (
          <div className="dashboard-plate__link-insert" />
        )}
      </div>
    </div>
  );
});

export default DashboardPlate;
